// Workaround for pinia 2.3.1 + @pinia/nuxt 0.9.0:
// `shouldHydrate(obj)` calls `obj.hasOwnProperty(skipHydrateSymbol)`
// directly. With SSR enabled, devalue's stringify walks every value in
// the payload through our reducer. The moment one value happens to have
// a non-function `hasOwnProperty` property (e.g. arbitrary user-typed
// content from an API like `description`, `name_ar`, etc.), the call
// crashes the entire render with "obj.hasOwnProperty is not a function".
//
// We re-register the `skipHydrate` payload reducer with the safe idiom
// (`Object.prototype.hasOwnProperty.call(...)`). Same keys → ours wins.
//
// Remove this plugin once pinia ships the fix upstream
// (https://github.com/vuejs/pinia/issues — search shouldHydrate).

const SKIP_HYDRATE_SYMBOL = Symbol.for('pinia:skipHydration');

const isPlainObject = (o: unknown): o is Record<string, unknown> =>
  !!o
  && typeof o === 'object'
  && Object.prototype.toString.call(o) === '[object Object]'
  && typeof (o as { toJSON?: unknown }).toJSON !== 'function';

const safeShouldHydrate = (obj: unknown): boolean => {
  if (!isPlainObject(obj)) return true;
  // Safe call — never relies on the obj having `hasOwnProperty` as a method.
  return !Object.prototype.hasOwnProperty.call(obj, SKIP_HYDRATE_SYMBOL);
};

export default defineNuxtPlugin(() => {
  definePayloadReducer('skipHydrate', (data: unknown) => !safeShouldHydrate(data) && 1);
  definePayloadReviver('skipHydrate', () => undefined);
});
